/**
 * BranchSwitcher — "Working at: Osu". Shown when the person may work at
 * more than one branch of the open business. The pick is sent as
 * X-Branch-Id; the server checks it, and branch roles only count there (a
 * cashier at Osu who supervises at Tema gets supervisor screens only at
 * Tema). Sales go to that branch's stock.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import useBusinessStore from "@/stores/businessStore";
import { accessKeys, accessService } from "@/services/accessService";

export function BranchSwitcher() {
  const businessId = useBusinessStore((s) => s.activeBusinessId);
  const picked = useBusinessStore((s) => (businessId ? s.activeBranchByBusiness?.[businessId] ?? null : null));
  const setActiveBranch = useBusinessStore((s) => s.setActiveBranch);
  const places = useQuery({ queryKey: accessKeys.myPlaces(), queryFn: accessService.myPlaces, staleTime: 5 * 60_000, retry: false });
  const here = places.data?.find((p) => p.businessId === businessId);
  // A pick that's no longer allowed (access changed) would lock them out — drop it.
  const stale = !!(picked && places.data && (!here || !here.branches.some((b) => b._id === picked)));
  useEffect(() => {
    if (stale && businessId) setActiveBranch(businessId, null);
  }, [stale, businessId, setActiveBranch]);
  if (!businessId || !here || here.branches.length < 2) return null;
  const current = here.branches.find((b) => b._id === picked) || here.branches.find((b) => b._id === here.homeBranchId) || null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 gap-1.5 px-2.5 hover:bg-muted" aria-label="Branch you're working at">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          {/* Phones: the pin only (the name is in the menu) — keeps the top bar from spilling sideways. */}
          <span className="hidden max-w-[140px] truncate text-sm sm:inline">{current ? current.name : "All branches"}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Working at</DropdownMenuLabel>
        {here.wholeBusiness && (
          <DropdownMenuItem onClick={() => setActiveBranch(businessId, null)}>
            <span className="flex-1">{here.homeBranchId ? "My home branch" : "All branches"}</span>
            {!picked && <Check className="h-3.5 w-3.5" />}
          </DropdownMenuItem>
        )}
        {here.wholeBusiness && <DropdownMenuSeparator />}
        {here.branches.map((b) => (
          <DropdownMenuItem key={b._id} onClick={() => setActiveBranch(businessId, b._id)}>
            <div className="min-w-0 flex-1">
              <div className="truncate">{b.name}</div>
              {b.roles.length > 0 && <div className="truncate text-[11px] text-muted-foreground">{b.roles.join(", ")}</div>}
            </div>
            {picked === b._id && <Check className="h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default BranchSwitcher;
